/**
 * Embed Login Page
 * Mobile-optimized login with multiple authentication options:
 * - Email/OTP (magic link)
 * - Google OAuth (popup)
 * - Continue as Guest
 * Uses centralized theme system
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { debugLog } from '../debug';
// Note: appSettings not used for Cognito config in embed - fetched from API instead
import { useEmbedAuth } from '../providers/EmbedAuthProvider';
import { useEmbedConfig, useEmitEvent } from '../providers/EmbedConfigProvider';
import { useOwnerProfile } from '../providers/EmbedOwnerProfileProvider';
import { getWidgetHeaders } from '../services/widgetProxy';
import { useEmbedTheme } from '../theme';

// ============================================================================
// Module-level Cache
// ============================================================================

// Cache Cognito settings to avoid refetching on component remount
let cachedCognitoSettings: {
  url: string;
  clientId: string;
  callbackPath: string;
} | null = null;

// ============================================================================
// Types
// ============================================================================

type LoginMode = 'idle' | 'email' | 'otp' | 'loading' | 'guest';

interface LoginState {
  mode: LoginMode;
  email: string;
  otp: string;
  session: string | null;
  error: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

async function signInMagicLink(
  appBaseUrl: string,
  email: string,
  guestToken?: string | null,
): Promise<{ session: string }> {
  const url = `${appBaseUrl}/api/widget-proxy/auth/magic-link`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getWidgetHeaders(guestToken),
    body: JSON.stringify({ email }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to send verification code');
  }

  return result;
}

interface VerifyOTPResponse {
  id: string;
  fullName: string;
  email: string;
  isEmailVerified: boolean;
  jwt: string;
  refreshToken: string;
  profiles: Array<{
    id: string;
    Name: string;
    Email: string;
    DisplayPic: string;
  }>;
}

async function validateOTP(
  appBaseUrl: string,
  email: string,
  code: string,
  session: string,
  guestToken?: string | null,
): Promise<VerifyOTPResponse> {
  const url = `${appBaseUrl}/api/widget-proxy/auth/verify-otp`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getWidgetHeaders(guestToken),
    body: JSON.stringify({ email, code, session }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Invalid verification code');
  }

  return result;
}

// ============================================================================
// Icons
// ============================================================================

const BackIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const MailIcon: React.FC<{ color?: string; size?: number }> = ({ color, size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color || 'currentColor'}
    strokeWidth="1.5"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 7l-10 7L2 7" />
  </svg>
);

const LockIcon: React.FC<{ color?: string }> = ({ color }) => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color || 'currentColor'}
    strokeWidth="1.5"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const UserIcon: React.FC<{ color?: string }> = ({ color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color || 'currentColor'}
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// ============================================================================
// Helper
// ============================================================================

function appendMediaUrl(url: string | undefined, mediaBaseUrl: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${mediaBaseUrl}${url}`;
}

// ============================================================================
// Component
// ============================================================================

interface EmbedLoginPageProps {
  onContinueAsGuest?: () => void;
}

export const EmbedLoginPage: React.FC<EmbedLoginPageProps> = ({ onContinueAsGuest }) => {
  const { appBaseUrl, title, mediaBaseUrl, id, ownerHandle } = useEmbedConfig();
  const { theme, primaryColor } = useEmbedTheme();
  const { login, guestToken, guestTokenLoading, refetchGuestToken } = useEmbedAuth();
  const { ownerProfile, isLoading: isOwnerLoading } = useOwnerProfile();
  const emitEvent = useEmitEvent();

  // Refs for Google OAuth popup
  const popupRef = useRef<Window | null>(null);
  const popupCheckInterval = useRef<NodeJS.Timeout | null>(null);

  // Determine what to show as the title/branding
  const showLoadingState = (!!id || !!ownerHandle) && isOwnerLoading;
  const displayName = showLoadingState ? '' : ownerProfile?.Name || title;
  const displayImage = ownerProfile?.DisplayPic
    ? appendMediaUrl(ownerProfile.DisplayPic, mediaBaseUrl)
    : null;

  const [state, setState] = useState<LoginState>({
    mode: 'idle',
    email: '',
    otp: '',
    session: null,
    error: null,
  });

  // Cognito settings fetched from API (not baked into embed bundle)
  // Cache settings in module scope to avoid refetching on remount
  const [cognitoSettings, setCognitoSettings] = useState<{
    url: string;
    clientId: string;
    callbackPath: string;
  } | null>(cachedCognitoSettings);
  const [settingsLoading, setSettingsLoading] = useState(!cachedCognitoSettings);

  // Fetch Cognito settings from the app server (with caching)
  useEffect(() => {
    if (!appBaseUrl) return;

    // Use cached settings if available
    if (cachedCognitoSettings) {
      setCognitoSettings(cachedCognitoSettings);
      setSettingsLoading(false);
      return;
    }

    const fetchSettings = async () => {
      setSettingsLoading(true);
      try {
        debugLog('[EmbedLogin] Fetching Cognito settings from:', `${appBaseUrl}/api/widget-proxy/settings`);
        const response = await fetch(`${appBaseUrl}/api/widget-proxy/settings`);
        if (response.ok) {
          const data = await response.json();
          debugLog('[EmbedLogin] Cognito settings received:', data.cognito);
          cachedCognitoSettings = data.cognito; // Cache for future use
          setCognitoSettings(data.cognito);
        } else {
          console.error('[EmbedLogin] Failed to fetch settings:', response.status);
        }
      } catch (error) {
        console.error('[EmbedLogin] Error fetching settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [appBaseUrl]);

  const isLoading = state.mode === 'loading';
  const canSubmitEmail = !guestTokenLoading && !!guestToken && !!state.email;

  // Debug logging for email submit
  debugLog(
    '[EmbedLogin] canSubmitEmail:',
    canSubmitEmail,
    '- guestTokenLoading:',
    guestTokenLoading,
    'guestToken:',
    !!guestToken,
    'email:',
    state.email,
  );

  // Handle Google OAuth popup message
  const handlePopupMessage = useCallback(
    async (event: MessageEvent) => {
      debugLog('[EmbedLogin] Received postMessage:', event.origin, event.data?.type);

      // Check if this is our auth message
      if (event.data?.type === 'GOOGLE_AUTH_SUCCESS') {
        debugLog('[EmbedLogin] Google auth success received');
        const { loginData, profileData } = event.data;
        debugLog('[EmbedLogin] loginData:', loginData ? 'present' : 'missing');
        debugLog('[EmbedLogin] profileData:', profileData);

        if (loginData?.id_token) {
          const userData = {
            id: profileData?.id || '',
            email: profileData?.Email || '',
            fullName: profileData?.fullName || profileData?.Name || '',
            displayPic: profileData?.DisplayPic,
          };
          debugLog('[EmbedLogin] Logging in user:', userData.email);
          debugLog('[EmbedLogin] Full userData:', userData);

          login({
            jwt: loginData.id_token,
            refreshToken: loginData.refresh_token,
            user: userData,
          });

          emitEvent('auth:login', { user: userData, method: 'google' });
          debugLog('[EmbedLogin] Login complete, event emitted');
        } else {
          console.error('[EmbedLogin] No id_token in loginData');
        }

        // Clean up
        if (popupCheckInterval.current) {
          clearInterval(popupCheckInterval.current);
        }
      } else if (event.data?.type === 'GOOGLE_AUTH_ERROR') {
        console.error('[EmbedLogin] Google auth error:', event.data.error);
      }
    },
    [login, emitEvent],
  );

  // Set up message listener for Google OAuth popup
  useEffect(() => {
    window.addEventListener('message', handlePopupMessage);
    return () => {
      window.removeEventListener('message', handlePopupMessage);
      if (popupCheckInterval.current) {
        clearInterval(popupCheckInterval.current);
      }
    };
  }, [handlePopupMessage]);

  // Handle Google login via popup
  const handleGoogleLogin = useCallback(() => {
    debugLog('[EmbedLogin] Google login clicked');
    debugLog('[EmbedLogin] appBaseUrl:', appBaseUrl);
    debugLog('[EmbedLogin] cognitoSettings:', cognitoSettings);
    debugLog('[EmbedLogin] window.location.origin:', window.location.origin);

    if (!appBaseUrl) {
      console.error('[EmbedLogin] appBaseUrl is not set, aborting');
      return;
    }

    if (!cognitoSettings?.url || !cognitoSettings?.clientId) {
      console.error('[EmbedLogin] Cognito settings not loaded yet, aborting');
      setState((prev) => ({ ...prev, error: 'Authentication service not ready. Please try again.' }));
      return;
    }

    // Build OAuth state parameter - mark as embed widget popup
    const stateData = {
      isPopup: true,
      isEmbedWidget: true, // Prevents main app from logging in
      popupOrigin: window.location.origin,
    };
    const state = btoa(JSON.stringify(stateData));
    debugLog('[EmbedLogin] OAuth state data:', stateData);

    // Use appBaseUrl for redirect (this is the base app URL, e.g. spaces.qa.bldr.it)
    // This ensures OAuth callback goes to the app, not the embedding site (e.g. WordPress)
    const redirectUriBase = appBaseUrl;
    const callbackPath = cognitoSettings.callbackPath || 'callback';
    debugLog('[EmbedLogin] redirectUriBase:', redirectUriBase);
    debugLog('[EmbedLogin] callbackPath:', callbackPath);

    const requestUrl =
      `${cognitoSettings.url}/oauth2/authorize` +
      `?client_id=${cognitoSettings.clientId}` +
      `&redirect_uri=${redirectUriBase}/${callbackPath}` +
      `&scope=openid email` +
      `&response_type=code` +
      `&identity_provider=Google` +
      `&state=${encodeURIComponent(state)}`;
    debugLog('[EmbedLogin] OAuth request URL:', requestUrl);

    // Open popup
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    popupRef.current = window.open(
      requestUrl,
      'GoogleLogin',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
    );

    if (!popupRef.current) {
      console.error('[EmbedLogin] Popup blocked by browser');
      setState((prev) => ({ ...prev, error: 'Popup blocked. Please allow popups for this site.' }));
      return;
    }
    debugLog('[EmbedLogin] Popup opened successfully');

    // Monitor popup close
    popupCheckInterval.current = setInterval(() => {
      if (popupRef.current?.closed) {
        debugLog('[EmbedLogin] Popup was closed');
        if (popupCheckInterval.current) {
          clearInterval(popupCheckInterval.current);
        }
      }
    }, 500);

    emitEvent('auth:google_started', {});
    debugLog('[EmbedLogin] auth:google_started event emitted');
  }, [appBaseUrl, cognitoSettings, emitEvent]);

  // Handle Continue as Guest
  const handleContinueAsGuest = useCallback(() => {
    emitEvent('auth:guest', {});
    if (onContinueAsGuest) {
      onContinueAsGuest();
    }
  }, [emitEvent, onContinueAsGuest]);

  // Handle email submission
  const handleSubmitEmail = async () => {
    const email = state.email.trim();

    if (!email) {
      setState((prev) => ({ ...prev, error: 'Please enter your email address' }));
      return;
    }

    if (!appBaseUrl) {
      setState((prev) => ({ ...prev, error: 'App not configured properly' }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setState((prev) => ({ ...prev, error: 'Please enter a valid email address' }));
      return;
    }

    if (!guestToken) {
      setState((prev) => ({ ...prev, error: 'Connection not ready. Please try again.' }));
      refetchGuestToken();
      return;
    }

    setState((prev) => ({ ...prev, mode: 'loading', error: null }));

    try {
      const result = await signInMagicLink(appBaseUrl, email, guestToken);
      if (result?.session) {
        setState((prev) => ({
          ...prev,
          mode: 'otp',
          session: result.session,
          error: null,
        }));
        emitEvent('auth:otp_sent', { email });
      } else {
        throw new Error('Failed to send verification code');
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        mode: 'email',
        error: error.message || 'Failed to send verification code',
      }));
      emitEvent('auth:error', { error: error.message });
    }
  };

  // Handle OTP submission
  const handleSubmitOtp = async () => {
    const { email, otp, session } = state;
    if (!otp || !session || !appBaseUrl) return;

    setState((prev) => ({ ...prev, mode: 'loading', error: null }));

    try {
      const result = await validateOTP(appBaseUrl, email, otp, session, guestToken);
      if (result?.jwt) {
        const userData = {
          id: result.id,
          email: result.email,
          fullName: result.fullName || '',
          displayPic: result.profiles?.[0]?.DisplayPic,
        };

        login({
          jwt: result.jwt,
          refreshToken: result.refreshToken,
          user: userData,
        });

        emitEvent('auth:login', { user: userData });
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        mode: 'otp',
        error: error.message || 'Invalid verification code',
      }));
      emitEvent('auth:error', { error: error.message });
    }
  };

  const handleBack = () => {
    setState((prev) => ({
      ...prev,
      mode: 'email',
      otp: '',
      session: null,
      error: null,
    }));
  };

  const startLogin = () => {
    setState((prev) => ({ ...prev, mode: 'email', error: null }));
  };

  // ============================================================================
  // Render: Idle state - Welcome screen
  // ============================================================================

  if (state.mode === 'idle') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          backgroundColor: theme.colors.background.page,
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: theme.spacing['6'],
            overflowY: 'auto',
          }}
        >
          {/* Avatar/Icon */}
          {showLoadingState ? (
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: theme.borderRadius.full,
                margin: `0 auto ${theme.spacing['6']}`,
                backgroundColor: theme.colors.background.hover,
                animation: 'embed-pulse 1.5s ease-in-out infinite',
              }}
            />
          ) : displayImage ? (
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: theme.borderRadius.full,
                overflow: 'hidden',
                margin: `0 auto ${theme.spacing['6']}`,
                border: `3px solid ${primaryColor}`,
              }}
            >
              <img
                src={displayImage}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ) : (
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: theme.borderRadius.full,
                margin: `0 auto ${theme.spacing['6']}`,
                backgroundColor: theme.colors.palette.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MailIcon color={primaryColor} />
            </div>
          )}

          {/* Subtitle */}
          <p
            style={{
              fontSize: theme.typography.fontSize.base,
              color: theme.colors.text.secondary,
              textAlign: 'center',
              margin: `0 0 ${theme.spacing['6']} 0`,
            }}
          >
            Sign in to save your conversation
          </p>

          {/* Sign in options container */}
          <div
            style={{
              backgroundColor: theme.colors.background.elevated,
              borderRadius: theme.borderRadius.xl,
              padding: theme.spacing['4'],
              marginBottom: theme.spacing['4'],
            }}
          >
            {/* Two buttons - Email and Google */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: theme.spacing['3'],
              }}
            >
              {/* Email button */}
              <button
                onClick={startLogin}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing['2'],
                  padding: `${theme.spacing['3']} ${theme.spacing['4']}`,
                  backgroundColor: theme.colors.background.surface,
                  color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.subtle}`,
                  borderRadius: theme.borderRadius.lg,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: 'pointer',
                  minHeight: '48px',
                  transition: `all ${theme.animation.duration.fast}`,
                }}
              >
                <MailIcon color={theme.colors.text.secondary} size={20} />
                <span>Email</span>
              </button>

              {/* Google button */}
              <button
                onClick={handleGoogleLogin}
                disabled={settingsLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing['2'],
                  padding: `${theme.spacing['3']} ${theme.spacing['4']}`,
                  backgroundColor: theme.colors.background.surface,
                  color: settingsLoading ? theme.colors.text.tertiary : theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.subtle}`,
                  borderRadius: theme.borderRadius.lg,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: settingsLoading ? 'wait' : 'pointer',
                  minHeight: '48px',
                  transition: `all ${theme.animation.duration.fast}`,
                  opacity: settingsLoading ? 0.7 : 1,
                }}
              >
                <GoogleIcon />
                <span>{settingsLoading ? 'Loading...' : 'Google'}</span>
              </button>
            </div>
          </div>

          {/* Continue as Guest section */}
          <div style={{ marginTop: theme.spacing['4'] }}>
            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing['3'],
                marginBottom: theme.spacing['3'],
              }}
            >
              <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border.subtle }} />
              <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                or
              </span>
              <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border.subtle }} />
            </div>

            {/* Guest option box */}
            <div
              style={{
                backgroundColor: theme.colors.background.elevated,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing['4'],
                border: `1px solid ${theme.colors.border.subtle}`,
              }}
            >
              <button
                onClick={handleContinueAsGuest}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing['2'],
                  padding: `${theme.spacing['3']} ${theme.spacing['4']}`,
                  backgroundColor: theme.colors.background.surface,
                  color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.subtle}`,
                  borderRadius: theme.borderRadius.lg,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: 'pointer',
                  minHeight: '48px',
                  transition: `all ${theme.animation.duration.fast}`,
                }}
              >
                <UserIcon color={theme.colors.text.secondary} />
                <span>Continue as Guest</span>
              </button>

              {/* Guest limitations */}
              <div style={{ marginTop: theme.spacing['3'] }}>
                <p
                  style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.text.secondary,
                    fontWeight: theme.typography.fontWeight.medium,
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing['1'],
                    margin: 0,
                  }}
                >
                  <span>⚠️</span> Guest limitations:
                </p>
                <ul
                  style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.text.tertiary,
                    margin: `${theme.spacing['1']} 0 0 ${theme.spacing['5']}`,
                    padding: 0,
                    listStyle: 'disc',
                  }}
                >
                  <li>Conversation lost when tab closes</li>
                  <li>Cannot save or share</li>
                  <li>Session only</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Error display */}
          {state.error && (
            <div
              style={{
                marginTop: theme.spacing['4'],
                padding: theme.spacing['3'],
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${theme.colors.border.error}`,
                borderRadius: theme.borderRadius.md,
                color: theme.colors.text.error,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {state.error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Email input state
  // ============================================================================

  if (state.mode === 'email' || (state.mode === 'loading' && !state.session)) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          backgroundColor: theme.colors.background.page,
          fontFamily: theme.typography.fontFamily,
          overflowY: 'auto',
        }}
      >
        {/* Header with back button */}
        <div style={{ padding: theme.spacing['4'] }}>
          <button
            onClick={() => setState((prev) => ({ ...prev, mode: 'idle', error: null }))}
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: theme.borderRadius.full,
              backgroundColor: 'transparent',
              color: theme.colors.text.primary,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <BackIcon />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing['6'],
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: theme.borderRadius.full,
              margin: `0 auto ${theme.spacing['6']}`,
              backgroundColor: theme.colors.palette.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MailIcon color={primaryColor} />
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: theme.typography.fontSize['2xl'],
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.text.primary,
              textAlign: 'center',
              margin: `0 0 ${theme.spacing['2']} 0`,
            }}
          >
            Enter your email
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontSize: theme.typography.fontSize.base,
              color: theme.colors.text.secondary,
              textAlign: 'center',
              margin: `0 0 ${theme.spacing['8']} 0`,
            }}
          >
            We&apos;ll send you a verification code
          </p>

          {/* Error */}
          {state.error && (
            <div
              style={{
                padding: theme.spacing['3'],
                marginBottom: theme.spacing['4'],
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${theme.colors.border.error}`,
                borderRadius: theme.borderRadius.md,
                color: theme.colors.text.error,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {state.error}
            </div>
          )}

          {/* Email Input */}
          <input
            type="email"
            placeholder="Email address"
            value={state.email}
            onChange={(e) => setState((prev) => ({ ...prev, email: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleSubmitEmail()}
            disabled={isLoading}
            style={{
              width: '100%',
              height: theme.components.input.height,
              padding: theme.components.input.padding,
              marginBottom: theme.spacing['4'],
              backgroundColor: theme.colors.background.input,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.subtle}`,
              borderRadius: theme.borderRadius.lg,
              fontSize: theme.typography.fontSize.base,
              fontFamily: theme.typography.fontFamily,
              outline: 'none',
            }}
          />

          {/* Submit Button */}
          <button
            onClick={handleSubmitEmail}
            disabled={isLoading || !canSubmitEmail}
            style={{
              width: '100%',
              height: theme.components.button.height.md,
              backgroundColor: isLoading || !canSubmitEmail ? theme.colors.text.tertiary : primaryColor,
              color: theme.colors.text.inverse,
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.medium,
              cursor: isLoading || !canSubmitEmail ? 'not-allowed' : 'pointer',
              opacity: isLoading || !canSubmitEmail ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Sending...' : guestTokenLoading ? 'Connecting...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: OTP input state
  // ============================================================================

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: theme.colors.background.page,
        fontFamily: theme.typography.fontFamily,
        overflowY: 'auto',
      }}
    >
      {/* Header with back button */}
      <div style={{ padding: theme.spacing['4'] }}>
        <button
          onClick={handleBack}
          disabled={isLoading}
          style={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.borderRadius.full,
            backgroundColor: 'transparent',
            color: theme.colors.text.primary,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <BackIcon />
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing['6'],
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: theme.borderRadius.full,
            margin: `0 auto ${theme.spacing['6']}`,
            backgroundColor: theme.colors.palette.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockIcon color={primaryColor} />
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: theme.typography.fontSize['2xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text.primary,
            textAlign: 'center',
            margin: `0 0 ${theme.spacing['4']} 0`,
          }}
        >
          Enter code
        </h1>

        {/* Email display */}
        <p
          style={{
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.primary,
            textAlign: 'center',
            margin: `0 0 ${theme.spacing['2']} 0`,
          }}
        >
          {state.email}
        </p>

        {/* Instructions */}
        <p
          style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.secondary,
            textAlign: 'center',
            margin: `0 0 ${theme.spacing['6']} 0`,
          }}
        >
          Enter the 6-digit code sent to your email
        </p>

        {/* Error */}
        {state.error && (
          <div
            style={{
              padding: theme.spacing['3'],
              marginBottom: theme.spacing['4'],
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${theme.colors.border.error}`,
              borderRadius: theme.borderRadius.md,
              color: theme.colors.text.error,
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {state.error}
          </div>
        )}

        {/* OTP Input */}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="000000"
          value={state.otp}
          onChange={(e) =>
            setState((prev) => ({
              ...prev,
              otp: e.target.value.replace(/\D/g, '').slice(0, 6),
            }))
          }
          onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleSubmitOtp()}
          disabled={isLoading}
          style={{
            width: '100%',
            height: theme.components.input.height,
            padding: theme.components.input.padding,
            marginBottom: theme.spacing['4'],
            backgroundColor: theme.colors.background.input,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.subtle}`,
            borderRadius: theme.borderRadius.lg,
            fontSize: theme.typography.fontSize['2xl'],
            fontFamily: theme.typography.fontFamily,
            fontWeight: theme.typography.fontWeight.semibold,
            textAlign: 'center',
            letterSpacing: '8px',
            outline: 'none',
          }}
        />

        {/* Submit Button */}
        <button
          onClick={handleSubmitOtp}
          disabled={isLoading || state.otp.length !== 6}
          style={{
            width: '100%',
            height: theme.components.button.height.md,
            backgroundColor: isLoading || state.otp.length !== 6 ? theme.colors.text.tertiary : primaryColor,
            color: theme.colors.text.inverse,
            border: 'none',
            borderRadius: theme.borderRadius.lg,
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: isLoading || state.otp.length !== 6 ? 'not-allowed' : 'pointer',
            opacity: isLoading || state.otp.length !== 6 ? 0.6 : 1,
          }}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
      </div>
    </div>
  );
};

export default EmbedLoginPage;
